<ng-container *transloco="let t; read: 'configuration-settings-component'">
  @if (isLoading) {
    <div class="configuration-settings__loading">
      <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
    </div>
  }
  @if (configuration.removed) {
    <div class="configuration-settings__deleted">
      {{ t('delete-header') }}
    </div>
  } @else {
    <div class="configuration-settings__form">
      <section class="configuration-settings__group">
        <h5>{{ t('response-type') }}</h5>
        <div (change)="updateResponseType()">
          @for (response of responseRadios; track response) {
            <div class="configuration-settings__response-row">
              <div [matTooltip]="responseTypeTooltip">
                <xi-radio
                  name="responseRadio"
                  [value]="response.value"
                  [label]="response.label"
                  [disabled]="isReadOnly || hasAutoAnalysis || isLoading"
                  [(ngModel)]="responseType"
                ></xi-radio>
              </div>
              @if (response.value === 'CALLBACK') {
                <div class="configuration-settings__url">
                  <xi-icon-button
                    icon="edit"
                    [disabled]="isReadOnly || isLoading || responseType !== 'CALLBACK'"
                    (onClick)="onEditCallbackUrl()"
                  ></xi-icon-button>
                  <mat-form-field>
                    <input
                      matInput
                      readonly
                      placeholder="URL"
                      [disabled]="isReadOnly || isLoading || responseType !== 'CALLBACK'"
                      [ngModel]="configuration.callback_url"
                    />
                  </mat-form-field>
                </div>
              }
            </div>
          }
        </div>
        <ng-container
          *ngTemplateOutlet="tGroupActions; context: { infoSection: 'responseType' }"
        ></ng-container>
      </section>
      <section class="configuration-settings__group">
        <h5>{{ t('document-settings') }}</h5>
        <div class="configuration-settings__row">
          <span>{{ t('alphanumeric-threshold') }}</span>
          <app-lex-numeric-input
            [minValue]="0"
            [maxValue]="100"
            [disabled]="isReadOnly || isLoading"
            [ngModel]="configuration.alphanumeric_threshold"
            (blur)="updateProperty('alphanumeric_threshold', $event)"
            (keydown.enter)="$event.target.blur()"
            suffix="%"
          >
          </app-lex-numeric-input>
        </div>
        <app-settings-row-slide
          [value]="hasSetting('FAIL_ON_LONG_SENTENCES')"
          (valueChange)="updateSetting('FAIL_ON_LONG_SENTENCES')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('fail-on-sentences') }}"
        >
          {{ t('fail-on-sentences') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('FLATTEN_ALL_UPPER_CASE')"
          (valueChange)="updateSetting('FLATTEN_ALL_UPPER_CASE')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('flatten-documents') }}"
        >
          {{ t('flatten-documents-char') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('PROCESS_HTML')"
          (valueChange)="updateSetting('PROCESS_HTML')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('well-formed') }}"
        >
          {{ t('well-formed') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('ONE_SENTENCE_MODE')"
          (valueChange)="updateSetting('ONE_SENTENCE_MODE')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('twitter-mode') }}"
        >
          {{ t('twitter-mode') }}
        </app-settings-row-slide>
        <ng-container
          *ngTemplateOutlet="tGroupActions; context: { infoSection: 'documentSettings' }"
        ></ng-container>
      </section>
      <section class="configuration-settings__group" id="topics-section">
        <h5>{{ t('topics') }}</h5>
        <div class="configuration-settings__row">
          <app-settings-row-slide
            [value]="hasSetting('CONCEPT_TOPICS')"
            (valueChange)="updateSetting('CONCEPT_TOPICS')"
            [hasPermission]="hasFeaturePermission('CONCEPT_TOPICS')"
            [disabled]="isReadOnly || isLoading"
            label="{{ t('concept-topics') }}"
            class="app-settings-row-slide--margin-right"
          >
            {{ t('concept-topics') }}"
          </app-settings-row-slide>
          <div>
            <span>{{ t('concept-topic-threshold') }}</span>
            <app-lex-numeric-input
              [minValue]="20"
              [maxValue]="100"
              [disabled]="isReadOnly || isLoading"
              [ngModel]="conceptTopicsThreshold"
              (blur)="updateProperty('concept_topics_threshold', $event)"
              (keydown.enter)="$event.target.blur()"
              suffix="%"
            >
            </app-lex-numeric-input>
          </div>
        </div>
        <div class="configuration-settings__row">
          <app-settings-row-slide
            [value]="hasSetting('QUERY_TOPICS')"
            (valueChange)="updateSetting('QUERY_TOPICS')"
            [hasPermission]="hasFeaturePermission('QUERY_TOPICS')"
            [disabled]="isReadOnly || isLoading"
            class="app-settings-row-slide--margin-right"
            label="{{ t('query-topics') }}"
          >
            {{ t('query-topics') }}
          </app-settings-row-slide>
          <app-settings-row-slide
            [value]="hasSetting('TOPIC_STEMMING')"
            (valueChange)="updateSetting('TOPIC_STEMMING')"
            [hasPermission]="hasFeaturePermission('TOPIC_STEMMING')"
            [disabled]="isReadOnly || isLoading"
            label="{{ t('stem-query-topics') }}"
          >
            {{ t('stem-query-topics') }}
          </app-settings-row-slide>
        </div>
        <app-settings-row-slide
          [value]="hasSetting('AUTO_CATEGORIES')"
          (valueChange)="updateSetting('AUTO_CATEGORIES')"
          [hasPermission]="hasFeaturePermission('AUTO_CATEGORIES')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('auto-categories') }}"
        >
          {{ t('auto-categories') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('DOC_CLASSIFICATION')"
          (valueChange)="updateSetting('DOC_CLASSIFICATION')"
          [hasPermission]="hasFeaturePermission('DOC_CLASSIFICATION')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('use-machine-learning') }}"
        >
          {{ t('use-machine-learning') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('TAXONOMY')"
          (valueChange)="updateSetting('TAXONOMY')"
          [hasPermission]="hasFeaturePermission('TAXONOMY')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('return-taxonomy') }}"
        >
          {{ t('return-taxonomy') }}
        </app-settings-row-slide>
        <ng-container
          *ngTemplateOutlet="tGroupActions; context: { infoSection: 'topics' }"
        ></ng-container>
      </section>
      <section class="configuration-settings__group">
        <h5>{{ t('entities') }}</h5>
        <div class="configuration-settings__row">
          <span>{{ t('confidence-threshold') }}</span>
          <app-lex-numeric-input
            [minValue]="1"
            [maxValue]="100"
            [disabled]="isReadOnly || isLoading"
            [ngModel]="configuration.entities_threshold"
            (blur)="updateProperty('entities_threshold', $event)"
            (keydown.enter)="$event.target.blur()"
            suffix="%"
          ></app-lex-numeric-input>
        </div>
        <app-settings-row-slide
          [value]="hasSetting('NAMED_ENTITIES')"
          (valueChange)="updateSetting('NAMED_ENTITIES')"
          [hasPermission]="hasFeaturePermission('NAMED_ENTITIES')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('detect-named-entities') }}"
        >
          {{ t('detect-named-entities') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('USER_ENTITIES')"
          (valueChange)="updateSetting('USER_ENTITIES')"
          [hasPermission]="hasFeaturePermission('USER_ENTITIES')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('detect-user-defined') }}"
        >
          {{ t('detect-user-defined') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('ENTITIES_STEMMING')"
          (valueChange)="updateSetting('ENTITIES_STEMMING')"
          [hasPermission]="hasFeaturePermission('ENTITIES_STEMMING')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('stem-user-defined') }}"
        >
          {{ t('stem-user-defined') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('ANAPHORA_RESOLUTION')"
          (valueChange)="updateSetting('ANAPHORA_RESOLUTION')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('anaphora-resolution') }}"
        >
          {{ t('anaphora-resolution') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('ENTITY_OVERLAP')"
          (valueChange)="updateSetting('ENTITY_OVERLAP')"
          [hasPermission]="hasFeaturePermission('ENTITY_OVERLAP')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('entity-overlap') }}"
        >
          {{ t('entity-overlap') }}
        </app-settings-row-slide>
        <ng-container
          *ngTemplateOutlet="tGroupActions; context: { infoSection: 'entities' }"
        ></ng-container>
      </section>
      <section class="configuration-settings__group">
        <h5>{{ t('sentiment') }}</h5>
        <app-settings-row-slide
          [value]="hasSetting('SENTIMENT_PHRASES')"
          (valueChange)="updateSetting('SENTIMENT_PHRASES')"
          [hasPermission]="hasFeaturePermission('SENTIMENT_PHRASES')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('return-sentiment') }}"
        >
          {{ t('return-sentiment') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('DETECT_PHRASES')"
          (valueChange)="updateSetting('DETECT_PHRASES')"
          [hasPermission]="hasFeaturePermission('DETECT_PHRASES')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('detect-sentiment-phrases') }}"
        >
          {{ t('detect-sentiment-phrases') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('OVERRIDE_SENTIMENT_DICTIONARY')"
          (valueChange)="updateSetting('OVERRIDE_SENTIMENT_DICTIONARY')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('built-in-dictionary') }}"
        >
          {{ t('built-in-dictionary') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('USE_CLAUSE_SENTIMENT')"
          (valueChange)="updateSetting('USE_CLAUSE_SENTIMENT')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('use-clause-sentiment') }}"
        >
          {{ t('use-clause-sentiment') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasPropertyValue('sentiment_sections', 'ALL')"
          (valueChange)="updateSectionLvlSentiment('sentiment_sections', $event)"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('section-level-sentiment') }}"
        >
          {{ t('section-level-sentiment') }}
        </app-settings-row-slide>
        <ng-container
          *ngTemplateOutlet="tGroupActions; context: { infoSection: 'sentiment' }"
        ></ng-container>
      </section>
      <section class="configuration-settings__group">
        <h5>{{ t('themes') }}</h5>
        <app-settings-row-slide
          [value]="hasSetting('THEMES')"
          (valueChange)="updateSetting('THEMES')"
          [hasPermission]="hasFeaturePermission('THEMES')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('extract-themes') }}"
        >
          {{ t('extract-themes') }}
        </app-settings-row-slide>
        <ng-container
          *ngTemplateOutlet="tGroupActions; context: { infoSection: 'themes' }"
        ></ng-container>
      </section>
      <section class="configuration-settings__group">
        <h5>{{ t('machine-learning') }}</h5>
        <div class="configuration-settings__row_tall">
          <span>{{ t('include-learning-for') }}</span>
          <div class="deep_models_selector">
            <xi-dropdown
              [data]="deepModelTypes"
              [disabled]="
                isReadOnly || isLoading || !isConfigEnglish || !hasFeaturePermission('DEEP_MODELS')
              "
              [stretch]="true"
              [value]="selectedDeepModel"
              [tooltip]="{ text: selectedDeepModel }"
              (onValueChange)="handleDeepModelChange($event.detail.selected.id)"
            ></xi-dropdown>
          </div>
          @if (!hasFeaturePermission('DEEP_MODELS')) {
            <a
              href="https://www.lexalytics.com/contact"
              target="_blank"
              class="configuration-settings__row-link--unavailable"
              >{{ t('contact-support') }}</a
            >
          }
          @if (hasFeaturePermission('DEEP_MODELS')) {
            <div class="deep_models__note">
              {{ t('model-enable') }}
            </div>
          }
        </div>
        <div class="configuration-settings__row">
          <span> {{ t('deep-model-threshold') }}</span>
          <app-lex-numeric-input
            [minValue]="0"
            [maxValue]="100"
            [disabled]="
              isReadOnly || isLoading || !hasFeaturePermission('DEEP_MODELS') || !isConfigEnglish
            "
            [ngModel]="configuration.deep_models_threshold"
            (blur)="updateProperty('deep_models_threshold', $event)"
            suffix="%"
            (keydown.enter)="$event.target.blur()"
          ></app-lex-numeric-input>
          @if (!hasFeaturePermission('DEEP_MODELS')) {
            <a
              href="https://www.lexalytics.com/contact"
              target="_blank"
              class="configuration-settings__row-link--unavailable"
              >{{ t('contact-support') }}</a
            >
          }
        </div>
        <app-settings-row-slide
          [value]="hasSetting('SENTIMENT_CLAUSE_MODELS')"
          (valueChange)="updateSetting('SENTIMENT_CLAUSE_MODELS')"
          [hasPermission]="hasFeaturePermission('SENTIMENT_CLAUSE_MODELS')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('clause-model-sentiment') }}"
        >
          {{ t('clause-model-sentiment') }}
        </app-settings-row-slide>
        <ng-container
          *ngTemplateOutlet="tGroupActions; context: { infoSection: 'machineLearning' }"
        ></ng-container>
      </section>
      <section class="configuration-settings__group">
        <h5>{{ t('other') }}</h5>
        <app-settings-row-slide
          [value]="hasSetting('BLACKLISTS')"
          (valueChange)="updateSetting('BLACKLISTS')"
          [hasPermission]="hasFeaturePermission('BLACKLISTS')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('blacklists') }}"
        >
          {{ t('blacklists') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('INTENTIONS')"
          (valueChange)="updateSetting('INTENTIONS')"
          [hasPermission]="hasFeaturePermission('INTENTIONS')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('intentions') }}"
        >
          {{ t('intentions') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('DETECT_LANGUAGE')"
          (valueChange)="updateSetting('DETECT_LANGUAGE')"
          [hasPermission]="hasFeaturePermission('DETECT_LANGUAGE')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('language') }}"
        >
          {{ t('language') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('OPINIONS')"
          (valueChange)="updateSetting('OPINIONS')"
          [disabled]="isReadOnly || isLoading"
          [hasPermission]="hasFeaturePermission('OPINIONS')"
          label="{{ t('calculate-opinions') }}"
        >
          {{ t('calculate-opinions') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('RELATIONS')"
          (valueChange)="updateSetting('RELATIONS')"
          [hasPermission]="hasFeaturePermission('RELATIONS')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('relationships') }}"
        >
          {{ t('relationships') }}
        </app-settings-row-slide>
        <app-settings-row-slide
          [value]="hasSetting('RETURN_SOURCE_TEXT')"
          (valueChange)="updateSetting('RETURN_SOURCE_TEXT')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('entire-source') }}"
        >
          {{ t('entire-source') }}
        </app-settings-row-slide>
        <div class="configuration-settings__row">
          <span> {{ t('number-summary') }} </span>
          <app-lex-numeric-input
            [minValue]="summaryLimit?.min_value"
            [maxValue]="summaryLimit?.max_value"
            [disabled]="isReadOnly || isLoading"
            [ngModel]="configuration.summary_size"
            (blur)="updateProperty('summary_size', $event)"
            (keydown.enter)="$event.target.blur()"
          ></app-lex-numeric-input>
        </div>
        <app-settings-row-slide
          [value]="hasSetting('MENTIONS')"
          (valueChange)="updateSetting('MENTIONS')"
          [hasPermission]="hasFeaturePermission('MENTIONS')"
          [disabled]="isReadOnly || isLoading"
          label="{{ t('return-etc') }}"
        >
          {{ t('return-etc') }}
        </app-settings-row-slide>
        <div>
          <div>{{ t('pos-to-return') }}</div>
          <div class="configuration-settings__pos-wrapper">
            @for (pos of posTypes; track pos) {
              <xi-checkbox
                color="CX"
                [label]="pos.label"
                [checked]="pos.checked"
                (onChange)="updatePartOfSpeech(pos.value)"
                [disabled]="isReadOnly || isLoading"
              ></xi-checkbox>
            }
          </div>
        </div>
        <ng-container
          *ngTemplateOutlet="tGroupActions; context: { infoSection: 'other' }"
        ></ng-container>
      </section>
    </div>
    <div class="configuration-settings__info">
      @if (!sectionInfo) {
        <div class="configuration-settings__info-placeholder">
          <h5>{{ t('what-settings') }}</h5>
          <p>
            <span
              >{{ t('more-info-1') }}
              <div class="icon-button-container">
                <xi-icon icon="info" color="CX" iconSize="large"></xi-icon>
              </div>
              {{ t('more-info-2') }}</span
            >
          </p>
          <p>{{ t('even-more-info') }}</p>
        </div>
      } @else {
        <div class="configuration-settings__info-section" @sectionInfoAnim>
          <div class="configuration-settings__info-section-header">
            <span>{{ sectionInfo.header }}</span>
            <div>
              <xi-icon-button icon="cancel" (onClick)="closeSectionInfo()"></xi-icon-button>
            </div>
          </div>
          <div class="configuration-settings__info-scroll" #infoScroll>
            <div class="configuration-settings__info-section-text">
              <div class="configuration-settings__info-section-summary">
                {{ sectionInfo.summary }}
                @if (sectionInfo.link) {
                  <div>
                    <a [href]="sectionInfo.link" target="_blank">{{ t('learn-more') }}</a>
                  </div>
                }
              </div>
            </div>
            <div class="configuration-settings__info-section-content">
              @for (setting of sectionInfo.settings; track setting) {
                <div class="configuration-settings__info-section-setting">
                  <h1>{{ setting.name }}</h1>
                  <p>{{ setting.description }}</p>
                  @if (setting.link) {
                    <a [href]="setting.link" target="_blank">{{ t('learn-more') }}</a>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  }

  <ng-template #tGroupActions let-infoSection="infoSection">
    <div
      class="configuration-settings__group-actions"
      [ngClass]="
        sectionInfo?.id === infoSection
          ? 'configuration-settings__group-actions--selected'
          : 'configuration-settings__group-actions--unselected'
      "
    >
      <xi-icon-button
        [icon]="sectionInfo?.id === infoSection ? 'info_filled' : 'info'"
        color="CX"
        buttonSize="large"
        (onClick)="openSectionInfo(infoSection)"
      >
      </xi-icon-button></div
  ></ng-template>
</ng-container>

<!--Callback URL Modal-->
@if (showCallbackUrlDialog) {
  <app-lex-xi-modal
    [isVisible]="showCallbackUrlDialog"
    maxWidth="512px"
    (close)="onCloseCallbackUrlDialog(null)"
  >
    <ng-template appXIModalContent>
      @if (showCallbackUrlDialog) {
        <app-callback-url-dialog
          [url]="configuration.callback_url"
          (modalClosed)="onCloseCallbackUrlDialog($event)"
        ></app-callback-url-dialog>
      }
    </ng-template>
  </app-lex-xi-modal>
}
